import type { StoreProduct } from "replo-runtime/shared/types";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { setProductData } from "@v1/reducers/commerce-reducer";
import { useEditorStore } from "@v1/store/store";
import { mapProductToStoreProduct } from "@v1/utils/product";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { productRouter } from "@v2/trpc/routes/product";
import { filterNulls } from "replo-utils/lib/array";

/**
 * Hook to fetch product data for the current store.
 */
export function useInfiniteStoreProducts(opts: {
  pageSize: number;
  searchTerm?: string;
}) {
  const { pageSize, searchTerm } = opts;
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const { dispatch } = useEditorStore();

  const queryResult = productRouter.list.useInfiniteQuery(
    projectId
      ? {
          projectId,
          limit: pageSize,
          writeThroughProductsSync:
            // Note (Noah, 2025-09-08): replo-hosted projects always pass write-through products sync,
            // since in order to create a v2 project users must have updated their Shopify scopes
            project?.hostingType === "replo" ||
            isFeatureEnabled("write-through-products-sync"),
          filters: {
            searchTerm: searchTerm ?? "",
          },
        }
      : skipToken,
    {
      enabled: Boolean(projectId),
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const products = React.useMemo((): StoreProduct[] => {
    const allProducts =
      queryResult.data?.pages?.flatMap((page) => page.products) ?? [];
    return filterNulls(allProducts.map(mapProductToStoreProduct));
  }, [queryResult.data]);

  // Note (Kevin, 2025-06-20): Maintain Redux side effect for components that depend on product data store
  // This replicates RTK Query's automatic Redux updates on query success
  React.useEffect(() => {
    if (products.length > 0) {
      dispatch(setProductData(products));
    }
  }, [products, dispatch]);

  return {
    ...queryResult,
    products,
  };
}
